<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDealsStore } from 'dashboard/stores/deals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import TwoColumnDetailLayout from 'dashboard/components-next/TwoColumnDetailLayout.vue';
import DealDetailSidebar from 'dashboard/components-next/Deals/DealDetail/DealDetailSidebar.vue';
import InPlaceConversationPanel from 'dashboard/components-next/Conversation/InPlaceConversationPanel.vue';
import LinkedEntityPane from 'dashboard/components-next/LinkedEntityPane.vue';
import TimelineChat from 'dashboard/components-next/Timeline/TimelineChat.vue';

const route = useRoute();
const router = useRouter();
const dealsStore = useDealsStore();

// Conversa aberta no painel da direita (no lugar do Historico)
const activeConversationId = ref(null);

const dealId = computed(() => Number(route.params.dealId));
const deal = ref(null);
const isFetching = ref(true);
const isUpdating = computed(() => dealsStore.uiFlags.updatingItem);

const fetchDeal = async () => {
  isFetching.value = true;
  try {
    deal.value = await dealsStore.show(dealId.value);
  } finally {
    isFetching.value = false;
  }
};

watch(dealId, fetchDeal);
onMounted(fetchDeal);

const goToDealsList = () => {
  router.push({
    name: 'deals_all',
    params: { accountId: route.params.accountId },
  });
};

const goBack = () => {
  if (activeConversationId.value) {
    activeConversationId.value = null;
    return;
  }
  // Volta pra tela de origem (todas as negociacoes ou pipeline kanban/lista)
  if (window.history.state?.back) {
    router.back();
    return;
  }
  router.push({
    name: 'deals_all',
    params: { accountId: route.params.accountId },
  });
};

// --- Historico recarrega sozinho quando a negociacao muda na coluna esquerda ---
const timelineChatRef = ref(null);
const handleLinkedUpdated = () => timelineChatRef.value?.refetch();

const handleDealUpdated = updatedDeal => {
  deal.value = updatedDeal;
  timelineChatRef.value?.refetch();
};
</script>

<template>
  <TwoColumnDetailLayout
    :is-loading="isFetching"
    :has-data="!!deal?.id"
    :storage-key="LOCAL_STORAGE_KEYS.DEAL_DETAIL_SIDEBAR_WIDTH"
  >
    <template #sidebar>
      <LinkedEntityPane
        :key="deal.id"
        :active-conversation-id="activeConversationId"
        @open-conversation="id => (activeConversationId = id)"
        @close-conversation="activeConversationId = null"
        @linked-updated="handleLinkedUpdated"
      >
        <DealDetailSidebar
          :deal="deal"
          :is-updating="isUpdating"
          @back="goBack"
          @updated="handleDealUpdated"
          @deleted="goToDealsList"
        />
      </LinkedEntityPane>
    </template>
    <template #timeline>
      <InPlaceConversationPanel
        v-if="activeConversationId"
        :conversation-id="activeConversationId"
      />
      <TimelineChat
        v-else
        ref="timelineChatRef"
        subject-type="Deal"
        :subject-id="deal.id"
      />
    </template>
  </TwoColumnDetailLayout>
</template>
